import React from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, Search, Shield, Menu, X, User, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isAuthenticated, getAuthUser } from '@/lib/api';

// Logo oficial do DOEM (com fundo transparente)
const LOGO_DOEM_URL = "/logo_doem.png";

export const Header = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('doem_theme') === 'dark';
    }
    return false;
  });
  const user = getAuthUser();
  const authenticated = isAuthenticated();

  React.useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('doem_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('doem_theme', 'light');
    }
  }, [darkMode]);

  return (
    <header className="bg-[#B91C1C] dark:bg-[#7F1D1D] text-white shadow-md sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo com efeito de brilho e texto DEMOAC */}
          <Link to="/" className="flex items-center gap-4 hover:opacity-90 transition-opacity">
            <div className="relative">
              <div className="absolute inset-0 bg-white/40 rounded-full blur-xl scale-125"></div>
              <img 
                src={LOGO_DOEM_URL}
                alt="DOEM - Diário Oficial de Acaiaca"
                className="h-16 w-auto relative z-10 drop-shadow-lg"
              />
            </div>
            <div className="relative hidden sm:flex flex-col justify-center">
              <div className="absolute inset-0 bg-[#FFD700]/20 blur-xl scale-125 rounded-lg"></div>
              <span className="relative z-10 text-[#FFD700] font-heading font-bold text-xl tracking-wide drop-shadow-[0_0_15px_rgba(255,215,0,0.7)]" style={{textShadow: '0 0 20px rgba(255,215,0,0.8), 0 0 40px rgba(255,215,0,0.4)'}}>
                DEMOAC
              </span>
              <span className="relative z-10 text-[#FFD700] font-heading text-[10px] tracking-widest drop-shadow-[0_0_10px_rgba(255,215,0,0.6)]" style={{textShadow: '0 0 15px rgba(255,215,0,0.6), 0 0 30px rgba(255,215,0,0.3)'}}>
                DIÁRIO ELETRÔNICO MUNICIPAL DE ACAIACA
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white/90 hover:text-white transition-colors flex items-center gap-2">
              <Newspaper size={18} />
              Edições
            </Link>
            <Link to="/buscar" className="text-white/90 hover:text-white transition-colors flex items-center gap-2">
              <Search size={18} />
              Buscar
            </Link>
            <Link to="/verificar" className="text-white/90 hover:text-white transition-colors flex items-center gap-2">
              <Shield size={18} />
              Autenticidade
            </Link>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              className="text-white/90 hover:text-white hover:bg-white/10"
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            
            {authenticated ? (
              <Link to="/admin">
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                  <User size={16} className="mr-2" />
                  {user?.nome?.split(' ')[0] || 'Admin'}
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                  Área Restrita
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <nav className="md:hidden py-4 border-t border-white/20 space-y-2">
            <Link to="/" className="block py-2 text-white/90 hover:text-white" onClick={() => setMenuOpen(false)}>
              Edições
            </Link>
            <Link to="/buscar" className="block py-2 text-white/90 hover:text-white" onClick={() => setMenuOpen(false)}>
              Buscar
            </Link>
            <Link to="/verificar" className="block py-2 text-white/90 hover:text-white" onClick={() => setMenuOpen(false)}>
              Autenticidade
            </Link>
            <button 
              onClick={() => setDarkMode(!darkMode)} 
              className="block py-2 text-white/90 hover:text-white w-full text-left"
            >
              {darkMode ? 'Modo Claro' : 'Modo Escuro'}
            </button>
            <Link to={authenticated ? "/admin" : "/login"} className="block py-2 text-white/90 hover:text-white" onClick={() => setMenuOpen(false)}>
              {authenticated ? 'Painel Admin' : 'Área Restrita'}
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#1F2937] dark:bg-[#111827] text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Logo com efeito de brilho e texto DEMOAC */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-full blur-xl scale-110"></div>
                <img 
                  src={LOGO_DOEM_URL}
                  alt="DEMOAC - Diário Eletrônico Municipal de Acaiaca"
                  className="h-20 w-auto relative z-10 drop-shadow-lg"
                />
              </div>
              {/* Texto DEMOAC com brilho */}
              <div className="relative flex flex-col justify-center">
                <div className="absolute inset-0 bg-[#FFD700]/15 blur-xl scale-125 rounded-lg"></div>
                <span className="relative z-10 text-[#FFD700] font-heading font-bold text-xl tracking-wide drop-shadow-[0_0_15px_rgba(255,215,0,0.7)]" style={{textShadow: '0 0 20px rgba(255,215,0,0.8), 0 0 40px rgba(255,215,0,0.4)'}}>
                  DEMOAC
                </span>
                <span className="relative z-10 text-[#FFD700] font-heading text-[9px] tracking-widest drop-shadow-[0_0_10px_rgba(255,215,0,0.6)]" style={{textShadow: '0 0 15px rgba(255,215,0,0.6), 0 0 30px rgba(255,215,0,0.3)'}}>
                  DIÁRIO ELETRÔNICO MUNICIPAL DE ACAIACA
                </span>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              Publicação oficial dos atos normativos e administrativos do Município de Acaiaca - MG.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Links Úteis</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">Edições do Dia</Link></li>
              <li><Link to="/buscar" className="hover:text-white transition-colors">Pesquisa Avançada</Link></li>
              <li><Link to="/verificar" className="hover:text-white transition-colors">Verificar Autenticidade</Link></li>
              <li><a href="https://acaiaca.mg.gov.br" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Portal da Prefeitura</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Contato</h4>
            <address className="text-gray-400 text-sm not-italic space-y-2">
              <p>Prefeitura Municipal de Acaiaca - MG</p>
              <p>Praça Tancredo Neves, Nº 35</p>
              <p>Centro - Acaiaca/MG</p>
              <p>CEP: 35.438-000</p>
              <p className="pt-2">
                <strong className="text-white">Tel:</strong> (31) 3887-1650
              </p>
              <p>
                <strong className="text-white">E-mail:</strong>{' '}
                <a href="mailto:administracao@acaiaca.mg.gov.br" className="hover:text-white transition-colors">
                  administracao@acaiaca.mg.gov.br
                </a>
              </p>
            </address>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Prefeitura Municipal de Acaiaca - MG. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] dark:bg-[#111827]">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};
